import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import {
  REFRESH_TOKEN_COOKIE_KEY,
  REFRESH_TOKEN_LOCAL_STORAGE_KEY,
  SESSION_TOKEN_COOKIE_KEY,
  SESSION_TOKEN_LOCAL_STORAGE_KEY,
} from '@/lib/config'

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';')?.shift()
}

export const deleteCookie = (name: string, domain = false) => {
  let domainName = ''
  if (!domain) {
    if (getEnvironment() === 'development') {
      domainName = 'localhost'
    } else {
      domainName = 'productioncrate.com'
    }
  }
  document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/' + (domain ? '; domain=' + domainName : '')
}

export const setCookie = (name: string, value: string, days: number, domain: any = false) => {
  if (!domain) {
    if (getEnvironment() === 'development') {
      domain = 'localhost'
    } else {
      domain = '.productioncrate.com'
    }
  }
  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }
  // Seteando Secure y SameSite=None si no estamos en localhost (porque requiere HTTPS)
  let secure = ''
  let sameSite = ''
  if (domain !== 'localhost') {
    secure = '; Secure'
    sameSite = '; SameSite=None'
  }
  // Setear la cookie con los atributos adicionales
  document.cookie =
    name + '=' + (value || '') + expires + '; path=/' + (domain ? '; domain=' + domain : '') + sameSite + secure
}

export const getPersistedValue = (localStorageKey: string, cookieKey: string) => {
  let localStorageValue = localStorage.getItem(localStorageKey)
  let cookieValue = getCookie(cookieKey)

  console.debug('get persisted value', localStorageKey, cookieKey, localStorageValue === cookieValue)

  if (cookieValue && cookieValue != localStorageValue) {
    console.debug('Persisted values differences (wrong localstorage)')
    setPersistedValue(localStorageKey, cookieKey, cookieValue)
    return cookieValue
  }

  if (localStorageValue && cookieValue != localStorageValue) {
    console.debug('Persisted values differences (wrong/no cookie)')
    setPersistedValue(localStorageKey, cookieKey, localStorageValue)
    return localStorageValue
  }

  return localStorageValue || cookieValue
}

export const setPersistedValue = (localStorageKey: string, cookieKey: string, value: string) => {
  if (!value) {
    //console.log("Got a falsy value for "+localStorageKey+"/"+cookieKey+" removing...");
    localStorage.removeItem(localStorageKey)
    deleteCookie(cookieKey)
    return
  }
  console.debug('Persisting value in ', localStorageKey, cookieKey)
  localStorage.setItem(localStorageKey, value)
  setCookie(cookieKey, value, 365)
}

export const getEnvironment = () =>
  window.location.hostname === 'localhost'
    ? 'development'
    : window.location.hostname === 'user-qa.productioncrate.com'
      ? 'qa'
      : 'production'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const sendGForm = (data: {
  username?: string
  email?: string
  plan?: string
  score?: string
  feedback?: string
}) => {
  console.debug('Sending form data:', data)

  // URL del formulario de Google Forms que termina en /formResponse
  const googleFormURL =
    'https://docs.google.com/forms/d/e/1FAIpQLScSRvd9dL4c8JUTdTzWH_hZUItIFXOKlakDVMY0MV3aq6I3Gg/formResponse'

  // IDs de los campos del formulario. Reemplázalos con los tuyos.
  const entryIDs = {
    username: 'entry.674977585',
    email: 'entry.790349041',
    plan: 'entry.1849155168',
    score: 'entry.1982830785',
    feedback: 'entry.1381715405',
  }

  // Crear objeto FormData y añadir los datos
  const formData = new FormData()
  formData.append(entryIDs.username, data.username ?? '')
  formData.append(entryIDs.email, data.email ?? '')
  formData.append(entryIDs.plan, data.plan ?? '')
  formData.append(entryIDs.score, data.score ?? '')
  formData.append(entryIDs.feedback, data.feedback ?? '')

  // Hacer una solicitud POST al formulario de Google
  return fetch(googleFormURL, {
    method: 'POST',
    body: formData,
    mode: 'no-cors', // Importante para evitar problemas de CORS con Google Forms
  })
}

/**
 * Parse keywords string into an array of keywords.
 * @param keywords
 */
export const parseKeywords = (keywords: string | string[] | undefined) => {
  if (!keywords) return []

  if (typeof keywords === 'string') {
    return keywords
      .replace(/\s/g, ',')
      .split(',')
      .map((keyword) => keyword.trim().toLowerCase())
  }

  return keywords
}

/**
 * Sleep for a given amount of time.
 */
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Get credentials stored locally.
 */
export const getCredentials = () => {
  const accessToken = getPersistedValue(SESSION_TOKEN_LOCAL_STORAGE_KEY, SESSION_TOKEN_COOKIE_KEY)
  const refreshToken = getPersistedValue(REFRESH_TOKEN_LOCAL_STORAGE_KEY, REFRESH_TOKEN_COOKIE_KEY)

  return { accessToken, refreshToken }
}

/**
 * Clear credentials
 */
export const clearCredentials = () => {
  localStorage.removeItem(SESSION_TOKEN_LOCAL_STORAGE_KEY)
  localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY)
  deleteCookie(SESSION_TOKEN_COOKIE_KEY)
  deleteCookie(REFRESH_TOKEN_COOKIE_KEY)
}

/**
 * Format a duration in seconds to a string in the format mm:ss.
 * @param duration
 */
export const formatDuration = (duration: number) => {
  const minutes = Math.floor(duration / 60)
  const seconds = Math.floor(duration % 60)
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
}

export const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let index = 0
  while (bytes >= 1024 && index < units.length - 1) {
    bytes /= 1024
    index++
  }
  return `${bytes.toFixed(2)} ${units[index]}`
}
